<template>
  <div class="reference-section">
    <!-- Section Header -->
    <div class="section-header-reference" @click="toggleExpanded" style="cursor: pointer;">
      <h3 class="section-title">
        <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
        </svg>
        Reference Documents
      </h3>
      <div class="header-controls">
        <h3 v-if="!isExpanded && documents.length > 0" class="section-title">
          {{ documents.length }} File{{ documents.length !== 1 ? 's' : '' }}
        </h3>
        <svg 
          class="expand-icon" 
          :class="{ 'rotate-180': isExpanded }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
        </svg>
      </div>
    </div>

    <!-- Expanded Content -->
    <div v-if="isExpanded" class="section-content">
      <!-- Description -->
      <p class="section-description">
        Upload supporting documents like transcripts, certificates, recommendation letters, or portfolio items.
        These will be included when downloading your CV with references.
      </p>

      <!-- Upload Component -->
      <ReferenceDocumentUpload
        :entry-id="entryId"
        :entry-type="entryType"
        @upload-success="handleUploadSuccess"
        @upload-error="handleUploadError"
      />

      <!-- Document List -->
      <ReferenceDocumentList
        v-if="documents.length > 0"
        :documents="documents"
        :entry-id="entryId"
        :entry-type="entryType"
        @document-deleted="handleDocumentDeleted"
        @documents-reordered="handleDocumentsReordered"
      />

      <!-- Collapse Button -->
      <div v-if="documents.length > 0" class="collapse-section">
        <button @click="toggleExpanded" class="collapse-button">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
          </svg>
          Collapse
        </button>
      </div>
    </div>

    <!-- Error Display -->
    <div v-if="error" class="error-display">
      <svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <span>{{ error }}</span>
      <button @click="clearError" class="error-close">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import ReferenceDocumentUpload from './ReferenceDocumentUpload.vue'
import ReferenceDocumentList from './ReferenceDocumentList.vue'

export default {
  name: 'ReferenceDocumentSection',
  components: {
    ReferenceDocumentUpload,
    ReferenceDocumentList
  },
  props: {
    entryId: {
      type: [String, Number],
      required: true
    },
    entryType: {
      type: String,
      required: true,
      validator: (value) => ['education', 'experience', 'activity'].includes(value)
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    initiallyExpanded: {
      type: Boolean,
      default: false
    }
  },
  emits: ['documents-updated'],
  data() {
    return {
      documents: [],
      isExpanded: false, // Always start collapsed
      isLoading: false,
      error: ''
    }
  },
  async mounted() {
    if (this.isAuthenticated && this.entryId) {
      await this.loadDocuments()
    }
  },
  watch: {
    entryId: {
      async handler(newId, oldId) {
        if (newId && newId !== oldId && this.isAuthenticated) {
          await this.loadDocuments()
        } else if (!newId) {
          // Clear documents when entry ID is removed/null
          this.documents = []
        }
      }
    },
    isAuthenticated: {
      async handler(newValue) {
        if (newValue && this.entryId) {
          await this.loadDocuments()
        } else if (!newValue) {
          this.documents = []
        }
      }
    }
  },
  methods: {
    getCvId() {
      // Get CV ID from the window configuration set by Rails
      if (typeof window !== 'undefined' && window.cvCreatorConfig && window.cvCreatorConfig.cvId) {
        return window.cvCreatorConfig.cvId
      }
      return null
    },

    async loadDocuments() {
      if (!this.entryId || !this.isAuthenticated) return

      const cvId = this.getCvId()
      if (!cvId) {
        console.warn('Cannot load reference documents: No CV ID available')
        return
      }

      this.isLoading = true
      this.error = ''

      try {
        const response = await fetch(`/api/cvs/${cvId}/${this.entryType}_entries/${this.entryId}/reference_documents`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': this.getCSRFToken()
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.documents = data.reference_documents || []
          this.emitDocumentsUpdate()
        } else {
          throw new Error('Failed to load documents')
        }
      } catch (error) {
        console.error('Error loading documents:', error)
        this.error = 'Failed to load reference documents'
      } finally {
        this.isLoading = false
      }
    },

    toggleExpanded() {
      this.isExpanded = !this.isExpanded
    },

    handleUploadSuccess(data) {
      // Refresh the document list
      this.loadDocuments()
      this.clearError()

      // Ensure section is expanded to show new documents
      if (!this.isExpanded) {
        this.isExpanded = true
      }
    },

    handleUploadError(error) {
      this.error = error.message || 'Upload failed'
    },

    handleDocumentDeleted(documentId) {
      this.documents = this.documents.filter(doc => doc.id !== documentId)
      this.emitDocumentsUpdate()
    },

    handleDocumentsReordered(newOrder) {
      this.documents = newOrder
      this.emitDocumentsUpdate()
    },

    emitDocumentsUpdate() {
      // Emit event for parent components
      this.$emit('documents-updated', {
        entryId: this.entryId,
        entryType: this.entryType,
        documentCount: this.documents.length,
        documents: this.documents
      })
    },

    clearError() {
      this.error = ''
    },

    getCSRFToken() {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    }
  }
}
</script>

<style scoped>
.reference-section {
  margin-top: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fafafa;
  overflow: hidden;
}

.section-header-reference {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: #FFFFFF;
  border-bottom: 1px solid #e5e7eb;
}

.section-header-reference .section-title {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: #a1a1aa;
  margin: 0;
  padding-left: 0;
  position: static;
  text-transform: none;
  letter-spacing: normal;
}

.section-header-reference .section-title::before {
  display: none;
}

.section-icon {
  width: 0.75rem;
  height: 0.75rem;
  color: #d4d4d8;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.document-summary {
  font-weight: 400;
}

.expand-icon {
  width: 1rem;
  height: 1rem;
  transition: transform 0.15s ease;
}

.section-content {
  padding: 1.25rem;
}

.section-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.collapse-section {
  margin-top: 1.5rem;
  text-align: center;
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;
}

.collapse-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
}

.collapse-button:hover {
  background: #f3f4f6;
  color: #374151;
}

.error-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: #fef2f2;
  border-top: 1px solid #fecaca;
  color: #dc2626;
  font-size: 0.875rem;
}

.error-icon {
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
}

.error-close {
  margin-left: auto;
  padding: 0.25rem;
  color: #dc2626;
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.error-close:hover {
  background: #fee2e2;
}

/* Dark mode support */
.dark .reference-section {
  background: #1f2937;
  border-color: #374151;
}

.dark .section-header {
  background: #374151;
  border-bottom-color: #4b5563;
}

.dark .section-header-reference .section-title {
  color: #d1d5db;
}

.dark .section-icon {
  color: #9ca3af;
}

.dark .expand-button {
  background: #1f2937;
  border-color: #4b5563;
  color: #9ca3af;
}

.dark .expand-button:hover {
  background: #374151;
  color: #d1d5db;
}

.dark .section-description {
  color: #9ca3af;
}

.dark .collapse-section {
  border-top-color: #4b5563;
}

.dark .collapse-button {
  background: #374151;
  border-color: #4b5563;
  color: #9ca3af;
}

.dark .collapse-button:hover {
  background: #4b5563;
  color: #d1d5db;
}

.dark .error-display {
  background: #7f1d1d;
  border-top-color: #dc2626;
  color: #fca5a5;
}

.dark .error-close:hover {
  background: rgba(220, 38, 38, 0.2);
}
</style>